import { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';

import {
  Snackbar,
  Typography,
  PopupsContext,
} from '@beeline/design-system-react/extracted-styles';
import { useShadowContainer } from '@beeline/lk-utils';

import { FeedbackDialog } from 'src/components/FeedbackDialog/FeedbackDialog';
import { FeedbackDialogProvider } from 'src/components/FeedbackDialog/FeedbackDialogProvider';
import { useFullscreenElement } from 'src/providers/FullscreenProvider/FullscreenProvider';
import { HostProvider } from 'src/providers/HostProvider';
import { SidesheetSlot } from 'src/providers/SidesheetSlot';
import { ProtectedRoute } from 'src/routes/ProtectedRoute/ProtectedRoute';
import { routes } from 'src/routes/routes';
import { getIsDesktop } from 'src/store/agent/selectors';
import { fetchLastBookings } from 'src/store/lastBookings/services';
import { getMessagesState } from 'src/store/messages/selectors';
import { messagesActions } from 'src/store/messages/slice';
import { getSidesheetMode } from 'src/store/sidesheet/selectors';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getEmployeeNumber } from 'src/store/user/selectors';

import classes from './Main.module.scss';
import { Segments } from './Segments/Segments';
import { AppTabs } from './Tabs/AppTabs';

const AppTitle: FC = () => {
  return (
    <div className={classes.title}>
      <Typography variant='h4'>Бронирование мест</Typography>
    </div>
  );
};

const Layout: FC = () => {
  const dispatch = useAppDispatch();

  const mode = useSelector(getSidesheetMode);
  const { message, isOpen, delay } = useSelector(getMessagesState);
  const isDesktop = useSelector(getIsDesktop);

  const closeMessage = useCallback(() => {
    dispatch(messagesActions.close());
  }, [dispatch]);

  return (
    <div className={classes.page} data-with-sidesheet={mode === 'standard'}>
      <div className={classes.container}>
        <div className={classes.header}>
          {isDesktop ? <AppTitle /> : <Segments />}

          {isDesktop && <AppTabs />}
        </div>

        <div className={classes.content}>
          <Routes>
            {routes.map(({ Element, path, permission }) => (
              <Route
                key={path}
                element={
                  permission ? (
                    <ProtectedRoute permission={permission}>
                      <Element />
                    </ProtectedRoute>
                  ) : (
                    <Element />
                  )
                }
                path={path}
              />
            ))}
          </Routes>
        </div>
      </div>

      <Snackbar
        delay={delay}
        horizontal='center'
        message={message}
        onClose={closeMessage}
        open={isOpen}
        variant='elastic'
      />
    </div>
  );
};

export const Main: FC = () => {
  const rootElement = useShadowContainer();
  const { fullscreen, fullscreenElement } = useFullscreenElement();

  const contextValue = useMemo((): {
    applicationElementID: string;
    rootElement: HTMLElement | ShadowRoot | null;
  } => {
    return {
      applicationElementID: 'app',
      rootElement: fullscreen ? fullscreenElement : rootElement,
    };
  }, [fullscreen, fullscreenElement, rootElement]);

  const employeeNumber = useSelector(getEmployeeNumber);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (employeeNumber) dispatch(fetchLastBookings(employeeNumber));
  }, [dispatch, employeeNumber]);

  return (
    <HostProvider>
      <PopupsContext.Provider value={contextValue}>
        <FeedbackDialogProvider>
          <FeedbackDialog />

          <SidesheetSlot
            id='tabsLevelModal'
            mode='modal'
            hasOverlay
            elevation='above'
          >
            <SidesheetSlot id='tabsLevel'>
              <Layout />
            </SidesheetSlot>
          </SidesheetSlot>
        </FeedbackDialogProvider>
      </PopupsContext.Provider>
    </HostProvider>
  );
};
